import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiMinus, FiPlus, FiShoppingBag } from 'react-icons/fi';
import { getCart, updateCartItemQuantity, removeFromCart, getCartTotal } from '../lib/cart';
import { proceedToCheckout } from '../lib/checkout';
import toast from 'react-hot-toast';
import { useCurrency } from '../context/CurrencyContext';

export default function CartSidebar({ isOpen, onClose }) {
  const { formatPrice, currency, exchangeRate } = useCurrency();
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCart(getCart());
    }
  }, [isOpen]);

  const updateQuantity = async (productId, newQuantity, variationId = null) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const updatedCart = updateCartItemQuantity(productId, newQuantity, variationId);
      setCart(updatedCart);

      // Dispatch event to update cart count in header
      window.dispatchEvent(new Event('cartUpdated'));

      toast.success('Cart updated');
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setIsUpdating(false);
    }
  };

  const removeItem = async (productId, variationId = null) => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const updatedCart = removeFromCart(productId, variationId);
      setCart(updatedCart);

      // Dispatch event to update cart count in header
      window.dispatchEvent(new Event('cartUpdated'));

      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setIsUpdating(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-900 shadow-xl transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h2 className="text-xl font-semibold text-white">Shopping Cart</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <FiShoppingBag size={64} className="text-gray-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-400 mb-2">Your cart is empty</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Add some beautiful dresses or jewelry to get started
                </p>
                <Link
                  to="/collections"
                  onClick={onClose}
                  className="px-6 py-3 bg-pink-500 text-black font-semibold rounded-md hover:bg-pink-600 transition-colors"
                >
                  Shop Now
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.items.map((item, index) => (
                  <div key={`${item.id}-${index}`} className="flex space-x-4 p-4 bg-gray-800 rounded-lg">
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">
                        {item.name}
                      </h3>
                      <p className="text-sm text-pink-500 font-semibold">
                        {formatPrice(item.price)}
                      </p>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1, item.variation_id)}
                            disabled={isUpdating || item.quantity <= 1}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiMinus size={16} />
                          </button>
                          <span className="text-sm text-white font-medium w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1, item.variation_id)}
                            disabled={isUpdating || (item.stock_quantity && item.quantity >= item.stock_quantity)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiPlus size={16} />
                          </button>
                        </div>

                        <button
                          onClick={() => removeItem(item.id, item.variation_id)}
                          disabled={isUpdating}
                          className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cart.items.length > 0 && (
            <div className="border-t border-gray-800 p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-white">Total:</span>
                <span className="text-xl font-bold text-pink-500">
                  {formatPrice(getCartTotal())}
                </span>
              </div>

              <div className="space-y-3">
                <Link
                  to="/cart"
                  onClick={onClose}
                  className="block w-full px-6 py-3 bg-gray-800 text-white font-semibold rounded-md hover:bg-gray-700 transition-colors text-center"
                >
                  View Cart
                </Link>
                <button
                  onClick={() => {
                    onClose();
                    proceedToCheckout(currency, exchangeRate);
                  }}
                  className="block w-full px-6 py-3 bg-pink-500 text-black font-semibold rounded-md hover:bg-pink-600 transition-colors text-center"
                >
                  Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
